import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-hand-heart-line',
    title: 'Nós executamos, você assina a obra',
    description: 'A Home Office Premium assume toda a execução do mobiliário. Você entrega o projeto ao cliente com sua assinatura — nós garantimos que vai ficar exatamente como você projetou.',
  },
  {
    icon: 'ri-layout-3-line',
    title: 'Suporte completo em 3D',
    description: 'Desenvolvemos o projeto 3D do mobiliário integrado ao seu projeto arquitetônico. Você apresenta ao cliente com total segurança — e nós executamos com fidelidade ao projeto.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Comissionamento garantido',
    description: 'Modelo de parceria com reserva técnica transparente. Você indica, a gente executa, você recebe. Sem conflito de interesse, sem contato direto com seu cliente sem sua autorização.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Suporte local em Caruaru e Agreste',
    description: 'Equipe própria de montagem, pós-venda e suporte técnico. Quando seu cliente precisar de ajuste, estamos a uma ligação de distância — e você não precisa se envolver.',
  },
];

const blocks = [
  {
    title: 'Nós Executamos, Você Assina a Obra. Parceria Garantida e Sem Dor de Cabeça.',
    text: [
      'Arquitetos e engenheiros que trabalham com projetos corporativos, clínicas, restaurantes e residências de alto padrão enfrentam sempre o mesmo problema: encontrar um fornecedor de mobiliário que execute com fidelidade ao projeto, no prazo combinado, sem criar problemas com o cliente final. A Home Office Premium resolve esse problema.',
      'Nosso modelo Turnkey é simples: você nos apresenta o projeto, nós desenvolvemos o mobiliário em 3D integrado ao seu layout, você aprova e apresenta ao cliente, nós fabricamos e instalamos. Você assina a obra, nós garantimos a execução. Sem você precisar gerenciar fornecedor, prazo de entrega ou reclamação de montagem.',
    ],
    img: '/media/ia/0357.webp',
    reverse: false,
  },
  {
    title: 'Suporte em 3D e Reserva Técnica Transparente',
    text: [
      'Desenvolvemos o projeto 3D do mobiliário integrado ao seu projeto arquitetônico ou de interiores. Você recebe arquivos em formato compatível com os principais softwares de renderização (SketchUp, AutoCAD, Revit) para apresentar ao cliente com total segurança visual. E quando o cliente aprovar, nós executamos com fidelidade milimétrica ao projeto.',
      'Nossa reserva técnica é transparente e combinada antes do início do projeto. Você sabe exatamente quanto vai receber, quando vai receber e como vai receber. Sem surpresas, sem negociação depois da obra entregue. E com garantia de que não vamos contatar seu cliente diretamente sem sua autorização — seu relacionamento é seu.',
    ],
    img: '/media/ia/0358.webp',
    reverse: true,
  },
  {
    title: 'Do Projeto à Chave na Mão: Modelo Turnkey Completo',
    text: [
      'O modelo Turnkey da Home Office Premium cobre todo o processo: desenvolvimento do projeto 3D do mobiliário, fabricação com materiais de primeira linha, logística de entrega, instalação pela nossa equipe própria, e pós-venda com suporte técnico. Você entrega a chave ao cliente com a certeza de que tudo está funcionando e com aparência exatamente como foi projetado.',
      'Atendemos projetos de qualquer escala: de um consultório individual a uma policlínica com 20 salas, de um escritório de advocacia a uma distribuidora com 500m² de área administrativa. Nossa equipe tem capacidade para executar múltiplos projetos simultaneamente — o que significa que você pode indicar quantos clientes quiser sem se preocupar com capacidade de entrega.',
    ],
    img: '/media/ia/0359.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0360.webp',
    alt: 'Projeto 3D de mobiliário corporativo',
  },
  {
    src: '/media/ia/0361.webp',
    alt: 'Equipe de montagem em ação',
  },
  {
    src: '/media/ia/0362.webp',
    alt: 'Projeto entregue chave na mão',
  },
  {
    src: '/media/ia/0363.webp',
    alt: 'Consultoria de materiais e acabamentos',
  },
];

export default function ParceirosPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Canal de Parceria para Arquitetos e Engenheiros | Home Office Premium',
    description: 'Canal de parceria para arquitetos e engenheiros: modelo Turnkey, suporte em 3D, reserva técnica transparente. Nós executamos, você assina a obra. Caruaru PE.',
    keywords: 'parceria arquiteto Recife, engenheiro mobiliário Petrolina, turnkey mobiliário Garanhuns, reserva técnica arquiteto Arcoverde, projeto 3D móveis Sertão PE, fornecedor mobiliário arquiteto Pernambuco, parceria mobiliário corporativo Agreste, fornecedor móveis arquiteto Nordeste, parceria marcenaria Recife, reserva técnica engenheiro Petrolina, turnkey mobiliário Caruaru, parceria arquiteto Bezerros',
    path: '/parceiros',
  });

  useSchemaLD({
    id: 'schema-parceiros',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Canal de Parceria para Arquitetos e Engenheiros | Home Office Premium',
      description: 'Canal de parceria para arquitetos e engenheiros: modelo Turnkey, suporte em 3D, reserva técnica transparente.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/parceiros`,
      about: {
        '@type': 'Service',
        name: 'Parceria para Arquitetos e Engenheiros',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0364.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Canal de Parceria · Arquitetos · Engenheiros
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Nós executamos, você assina a obra. Parceria garantida e sem dor de cabeça.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Modelo Turnkey completo: projeto 3D integrado ao seu layout, fabricação, instalação e pós-venda. Você entrega a chave ao cliente com a certeza de que tudo está exatamente como foi projetado.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Suporte em 3D · Reserva técnica transparente · Equipe própria de montagem · Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20sou%20arquiteto%2Fengenheiro%20e%20vim%20da%20LP%20de%20Parceiros.%20Quero%20conhecer%20o%20modelo%20de%20parceria%20da%20Home%20Office%20Premium!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              QUERO SER PARCEIRO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que arquitetos e engenheiros escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Modelo Turnkey</span> do Projeto à Chave na Mão
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20sou%20arquiteto%2Fengenheiro%20e%20vim%20da%20LP%20de%20Parceiros.%20Quero%20conhecer%20o%20modelo%20de%20parceria%20da%20Home%20Office%20Premium!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              QUERO CONHECER O MODELO DE PARCERIA
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Parceria exclusiva</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Seu próximo projeto de mobiliário entregue chave na mão.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Fale com nossa equipe comercial, apresente seu projeto e descubra como o modelo de parceria da Home Office Premium pode agregar valor ao seu escritório de arquitetura ou engenharia.
          </p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20sou%20arquiteto%2Fengenheiro%20e%20vim%20da%20LP%20de%20Parceiros.%20Quero%20conhecer%20o%20modelo%20de%20parceria%20da%20Home%20Office%20Premium!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO SER PARCEIRO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Projetos <span className="text-[#E8610A]">Turnkey Entregues</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">CANAL DE PARCERIA · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Vamos conversar?</h2>
          <p className="text-gray-500 text-base mb-8">Apresente seu projeto. Nossa equipe responde em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20sou%20arquiteto%2Fengenheiro%20e%20vim%20da%20LP%20de%20Parceiros.%20Quero%20conhecer%20o%20modelo%20de%20parceria%20da%20Home%20Office%20Premium!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20sou%20arquiteto%2Fengenheiro%20e%20vim%20da%20LP%20de%20Parceiros.%20Quero%20conhecer%20o%20modelo%20de%20parceria%20da%20Home%20Office%20Premium!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
